import { fromActor } from '@b2b/common/rxjs';
import { ApiService } from '@b2b/common/services/api/types';
import {
  XStateInspectorService,
  XStateInspectorServiceModule,
} from '@b2b/common/services/xstate/xstate-inspector.service';
import { extractACMEPayload, extractUnwrappedACMEError } from '@b2b/types/core';
import { Order, OrderEventItem } from '@b2b/types/order';
import angular from 'angular';
import { map } from 'rxjs';
import { createActor, fromPromise, StateFrom } from 'xstate';
import { ordersDetailMachine } from '../machines/orders-detail.machine';

interface OrdersDetailServiceProps {
  onOnlinePaymentLinkChanged: (params: { onlinePaymentUUID: string }) => void;
}

export class OrdersDetailServiceFactory {
  static $inject = ['ApiService', 'XStateInspectorService'];

  constructor(private apiService: ApiService, private xstateInspectorService: XStateInspectorService) {}

  /**
   * Create a service instance at the component level
   */
  createInstance(props: OrdersDetailServiceProps) {
    return new OrdersDetailService(this.apiService, this.xstateInspectorService, props);
  }
}

export class OrdersDetailService {
  private actor = this.createActor();
  state$ = fromActor(this.actor);
  vm$ = this.state$.pipe(map((state) => this.toVM(state)));

  constructor(
    private apiService: ApiService,
    private xstateInspectorService: XStateInspectorService,
    private props: OrdersDetailServiceProps
  ) {}

  destroy() {
    this.actor.stop();
  }

  getSnapshot() {
    return this.actor.getSnapshot();
  }

  toVM(state: StateFrom<typeof ordersDetailMachine>) {
    const isOrgOrder = !!state.context.organizationId;

    return {
      showCancelMembership: state.context.can.cancelMembership,
      showCreateOnlinePayment: isOrgOrder && !state.context.onlinePaymentUUID,
      onlinePaymentUUID: state.context.onlinePaymentUUID,
    };
  }

  async checkCancelMembershipEligility(order: Order) {
    const membership = this.findNewlyPurchasedMembershipInOrder(order);
    const membershipId = membership?.itemDetailId;
    const canCancelMembership = !membershipId
      ? false
      : await this.apiService.membership.eligibility
          .listByMembershipCategory({
            membershipId,
            saleChannels: 'insideSalesIndividual',
            actions: ['cancel'].join(),
            summarize: 0,
          })
          .then((response) => response.data.payload.canCancel)
          .catch(() => false);

    this.actor.send({
      type: 'SET_CANCEL_MEMBERSHIP_ELIGIBILITY',
      params: { orderId: order.orderId, canCancelMembership },
    });
  }

  private findNewlyPurchasedMembershipInOrder(order: Order): OrderEventItem | undefined {
    const reject = ['MembershipCancel', 'MembershipReplacement'];

    let newMembership: OrderEventItem | undefined;

    for (const eventItem of order.eventItems) {
      const newMembership = eventItem.items.find((item) => !reject.includes(item.itemType));

      if (newMembership) {
        break;
      }
    }

    return newMembership;
  }

  fetchOnlinePaymentLink(order: Pick<Order, 'orderNumber' | 'organizationId'>) {
    this.actor.send({
      type: 'FETCH_ONLINE_PAYMENT_LINK',
      params: {
        orderNumber: order.orderNumber,
        organizationId: order.organizationId ?? null,
      },
    });
  }

  createOnlinePaymentLink(order: Pick<Order, 'orderNumber'>) {
    this.actor.send({
      type: 'CREATE_ONLINE_PAYMENT_LINK',
      params: { orderNumber: order.orderNumber },
    });
  }

  deleteOnlinePayment(onlinePaymentUUID: string) {
    this.actor.send({
      type: 'DELETE_ONLINE_PAYMENT_LINK',
      params: { onlinePaymentUUID },
    });
  }

  generateOnlinePaymentLink(props: {
    brandedUrl: string | undefined;
    tenantId: string | undefined;
    consumerPage: string;
    onlinePaymentUUID: string;
  }): string {
    if (!props.onlinePaymentUUID) {
      return '';
    }

    if (props.brandedUrl) {
      return `${props.brandedUrl}/pay-balance/${props.tenantId}/${props.onlinePaymentUUID}`;
    }

    const consumerPage = props.consumerPage.replace(/events/gi, 'pay-balance');
    return `${consumerPage}/${props.onlinePaymentUUID}`;
  }

  private createActor() {
    const fetchPaymentLink = (orderNumber: string) => {
      return this.apiService.resource.metadata
        .get({ entityType: 'order', entityId: orderNumber })
        .then(extractACMEPayload())
        .then((payload) => ({ onlinePaymentUUID: payload.encryptedUUID }))
        .catch(() => ({ onlinePaymentUUID: '' }));
    };

    const inspector = this.xstateInspectorService.register(ordersDetailMachine.id);
    const actor = createActor(
      ordersDetailMachine.provide({
        actions: {},

        actors: {
          fetchPaymentLink: fromPromise(async ({ input }) => {
            if (!input.isOrgOrder) {
              return { onlinePaymentUUID: '' };
            }

            return fetchPaymentLink(input.orderNumber);
          }),
          createPaymentLink: fromPromise(async ({ input }) => {
            return this.apiService.resource.metadata
              .create({ entityType: 'order', entityId: input.orderNumber })
              .then(extractACMEPayload())
              .then((payload) => ({ onlinePaymentUUID: payload.encryptedUUID }))
              .catch((response) => {
                const errorData = extractUnwrappedACMEError(response);

                if (errorData.type === 'ENTITY_ID_AND_TYPE_ALREADY_EXIST') {
                  return fetchPaymentLink(input.orderNumber);
                }

                throw errorData;
              });
          }),
          deletePaymentLink: fromPromise(async ({ input }) => {
            return this.apiService.resource.metadata
              .delete({ onlinePaymentUUID: input.onlinePaymentUUID })
              .then(extractACMEPayload())
              .then(() => ({ onlinePaymentUUID: '' }))
              .catch((response) => {
                const errorData = extractUnwrappedACMEError(response);

                if (errorData.type === 'ENTITY_ID_NOT_FOUND') {
                  return { onlinePaymentUUID: '' };
                }

                throw errorData;
              });
          }),
        },
      }),
      {
        inspect: inspector?.inspect,
      }
    );

    actor.on('ONLINE_PAYMENT_LINK_CHANGED', (event) =>
      this.props.onOnlinePaymentLinkChanged({ onlinePaymentUUID: event.params.onlinePaymentUUID })
    );

    return actor.start();
  }
}

export const OrdersDetailServiceModule = angular
  .module('acme.view.application.orders.detail.service', ['acme.common.service.api', XStateInspectorServiceModule])
  .service('OrdersDetailServiceFactory', OrdersDetailServiceFactory).name;
